import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useRouteImpact, useRouteImpactMutation } from "./hooks";
import { fmtDate } from "../utils";

/**
 * Rykoszet przeprojektowania: nowa wersja trasy zmieniła przebieg na działkach z
 * postępem pozyskania (route_review). Projektant decyduje per działka (DEFAULT: TAK),
 * czy cofnąć ją do stanu startowego — reset toru z szablonu kategorii + statusu.
 * Odznaczone tylko czyszczą flagę (weryfikacja wykonana, uzgodnienie zostaje).
 */
export function RouteImpactModal({ connectionId, onClose }: {
  connectionId: string;
  onClose: () => void;
}) {
  const { data } = useRouteImpact(connectionId);
  const apply = useRouteImpactMutation(connectionId);
  const parcels = useMemo(() => data?.parcels ?? [], [data]);
  const [unchecked, setUnchecked] = useState<Set<string>>(new Set());

  const toggle = (id: string) => setUnchecked((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Modal open onClose={onClose} title="Przeprojektowanie — działki dotknięte zmianą trasy">
      <div className="space-y-3 text-sm">
        <p className="text-xs text-[var(--color-text-muted)]">
          Trasa zmieniła się na tych działkach, a miały już postęp pozyskania. Zaznaczone
          (domyślnie wszystkie) <b>cofną się do stanu startowego</b>: tor uzgodnienia
          wróci do szablonu kategorii, status działki przeliczy się od zera. Odznaczone
          zachowają uzgodnienie — flaga weryfikacji zostanie zdjęta jako „sprawdzone”.
        </p>
        {!parcels.length ? (
          <p className="text-xs text-[var(--color-text-muted)]">Brak działek do decyzji.</p>
        ) : (
          <div className="space-y-1.5">
            {parcels.map((p) => (
              <label key={p.parcel_id}
                className="flex items-center gap-2 border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-elevated)] text-xs cursor-pointer">
                <input type="checkbox" checked={!unchecked.has(p.parcel_id)}
                  onChange={() => toggle(p.parcel_id)} />
                <span className="font-mono font-bold">{p.teryt || p.short_number}</span>
                <span className="flex-1 truncate">{p.owner}</span>
                <Badge color="gray">{p.category_display || "—"}</Badge>
                <Badge color="blue">{p.status_display}</Badge>
                {p.route_review_at && (
                  <span className="font-mono text-[9px] text-[var(--color-text-muted)]">
                    zmiana {fmtDate(p.route_review_at)}
                  </span>
                )}
              </label>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button size="sm" loading={apply.isPending} disabled={!parcels.length}
            onClick={() => apply.mutate({
              revert: parcels.filter((p) => !unchecked.has(p.parcel_id)).map((p) => p.parcel_id),
              keep: parcels.filter((p) => unchecked.has(p.parcel_id)).map((p) => p.parcel_id),
            }, { onSuccess: onClose })}>
            Zastosuj decyzje
          </Button>
          <button className="text-xs text-[var(--color-text-muted)]" onClick={onClose}>później</button>
        </div>
      </div>
    </Modal>
  );
}
